import React, { useMemo } from 'react';
import {
  BodyText,
  Box,
  Button,
  Card,
  FormField,
  Input,
  Modal,
} from '@chargify/custom-ui-components';
import { useTranslation } from 'react-i18next';
import { SubscriptionDetailsMachineInterpreter } from '../../../../machines/subscription/types';
import { useSelector } from '@xstate/react';
import { subscriptionSelectors } from '../../../../machines/subscription/index';
import { CouponsList } from '../../../../components/CouponsList/CouponsList';
import {
  formatCouponValue,
  getCouponDescription,
} from '../../../../containers/CouponsContainer/utils';
import { Currency } from '@chargify/formatters';
import { CouponSummary } from './components/index';
import { getCouponErrorMsg } from './utils';

type AddCouponModal = {
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: () => void;
  /**
   * Catalog id
   */
  catalogId: number;
  /**
   * Subscription id
   */
  subscriptionId: number;
  /**
   * Subscription Details service
   */
  subscriptionDetailsService: SubscriptionDetailsMachineInterpreter;
  /**
   *
   */
  currency: Currency;
};

const Content = ({
  onClose,
  displayMode,
  subscriptionDetailsService,
  currency,
}: AddCouponModal) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const coupons = useSelector(
    subscriptionDetailsService,
    subscriptionSelectors.getCoupons
  );
  const couponError = useSelector(
    subscriptionDetailsService,
    subscriptionSelectors.getError
  );
  const addedCoupon = useSelector(
    subscriptionDetailsService,
    subscriptionSelectors.getAddedCoupon
  );

  const { isAddingCoupon, isCouponAdded } = useSelector(
    subscriptionDetailsService,
    subscriptionSelectors.getCouponsStatus
  );

  const [couponCode, setCouponCode] = React.useState<string>('');

  const formattedCoupons = useMemo(
    () =>
      coupons?.map((coupon) => ({
        code: coupon.code,
        value: formatCouponValue(coupon, currency),
        description: getCouponDescription(coupon, t),
      })),
    [coupons]
  );

  const handleAddCoupon = () => {
    subscriptionDetailsService.send({
      type: 'ADD_COUPON',
      couponCode,
    });
  };
  const handleAddAnotherCoupon = () => {
    setCouponCode('');
    subscriptionDetailsService.send({
      type: 'ADD_ANOTHER_COUPON',
    });
  };
  const modalTitle = isCouponAdded
    ? 'coupon_summary_title'
    : 'add_coupon_title';

  const couponErrorMsg = getCouponErrorMsg(couponError, t);

  return (
    <>
      <Card border={displayMode === 'layer' ? 'sharp' : 'rounded'}>
        <Card.Header>
          <Modal.Title onClose={onClose} title={t(`coupons.${modalTitle}`)} />
        </Card.Header>
        <Card.Body>
          {!addedCoupon ? (
            <>
              <Box marginBottom={5}>
                <BodyText>{t('coupons.applied_coupons')}</BodyText>
              </Box>
              <CouponsList coupons={formattedCoupons} />
              <Box marginTop={7} width={285} marginBottom={5}>
                <FormField
                  label={t('coupons.coupon_code')}
                  name={'couponCode'}
                  hasError={!!couponError}
                  error={couponErrorMsg}
                >
                  <Input
                    onChange={(e) => setCouponCode(e.target.value)}
                    name={'couponCode'}
                    value={couponCode}
                  />
                </FormField>
              </Box>
            </>
          ) : null}
          {addedCoupon ? (
            <CouponSummary
              currency={currency}
              onClick={handleAddAnotherCoupon}
              coupon={addedCoupon}
            />
          ) : null}
        </Card.Body>
        <Card.Footer>
          <Button.Group alignment="right">
            <Button
              color="secondary"
              label={t('back', { ns: 'common' })}
              onClick={onClose}
            />
            {!addedCoupon ? (
              <Button
                disabled={isAddingCoupon || !couponCode}
                isLoading={isAddingCoupon}
                color="primary"
                label={t('coupons.apply_coupon')}
                onClick={handleAddCoupon}
              />
            ) : null}
          </Button.Group>
        </Card.Footer>
      </Card>
    </>
  );
};

export default Content;
